import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { Controller, useForm } from 'react-hook-form';
import Validations from '../../utils/Validations';
import { createProduct, getProductById, updateProductById } from '../../services/Product';
import useToastr from '../../hooks/useToastr';

const CreateProduct = ({ title, onClose, editId, onSave }) => {
  const { control, handleSubmit, setValue } = useForm();
  const { showErrorToastr, showSuccessToastr } = useToastr();

  const [processing, setProcessing] = useState(false);

  const onSubmit = async (data) => {
    try {
      setProcessing(true);
      if (editId) {
        const result = await updateProductById(editId, {
          name: data.name,
          rate: Number(data.rate),
        });
        if (result.success) {
          showSuccessToastr('Product updated successfully.');
        } else {
          showErrorToastr(result?.data?.message || result?.message || 'Something went wrong.');
        }
        setProcessing(false);
        onSave();
      } else {
        const result = await createProduct({
          name: data.name,
          rate: Number(data.rate),
        });
        if (result.success) {
          showSuccessToastr('Product created successfully.');
        } else {
          showErrorToastr(result?.data?.message || result?.message || 'Something went wrong.');
        }
        setProcessing(false);
        onSave();
      }
      setProcessing(false);
    } catch (error) {
      showErrorToastr(error?.response?.data?.message || error?.message || 'Something went wrong.');
      setProcessing(false);
      onClose();
    }
  };

  useEffect(() => {
    if (editId) {
      getProductById(editId)
        .then((result) => {
          if (result.success) {
            const { data } = result;
            setValue('name', data.name);
            setValue('rate', data.rate);
          }
        })
        .catch((error) => {
          showErrorToastr(
            error?.response?.data?.message || error?.message || 'Something went wrong.'
          );
          onClose();
        });
    }
  }, [editId]);

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <form id="manage-product" onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" flexDirection="column">
            <Controller
              control={control}
              id="name"
              name="name"
              rules={{ ...Validations.REQUIRED }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  margin="dense"
                  label="Name"
                  variant="outlined"
                  focused
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error?.message : null}
                />
              )}
            />
            <Controller
              control={control}
              id="rate"
              name="rate"
              rules={{ ...Validations.REQUIRED }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  margin="dense"
                  type="number"
                  label="Rate"
                  focused
                  variant="outlined"
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error?.message : null}
                />
              )}
            />
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          type="submit"
          form="manage-product"
          autoFocus
          endIcon={processing && <CircularProgress size={25} />}
          disabled={processing}
          color="primary"
          variant="contained"
        >
          Save
        </Button>
        <Button onClick={onClose} variant="contained">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProduct;

CreateProduct.propTypes = {
  editId: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
